// config/planFeatures.ts
// Plan-based feature permissions matrix
// Single source of truth for which features are available in each plan

import { normalizePlanKey, type BasePlanKey } from './plans';

export type FeatureKey =
  | 'dashboard'
  | 'leads'
  | 'clients'
  | 'orders'
  | 'contracts'
  | 'users'
  | 'managers' // Alias for users
  | 'agents'
  | 'automations'
  | 'notifications'
  | 'settings'
  | 'profile'
  | 'sms'
  | 'unlimitedEverything';

export interface PlanFeatures {
  dashboard: boolean;
  leads: boolean;
  clients: boolean;
  orders: boolean;
  contracts: boolean;
  users: boolean;
  managers: boolean; // Alias for users
  agents: boolean;
  automations: boolean;
  notifications: boolean;
  settings: boolean;
  profile: boolean;
  sms: boolean;
  unlimitedEverything?: boolean;
}

/**
 * PLAN_FEATURES - Feature permissions for each plan
 * Controls which pages/features are accessible in each plan tier
 */
export const PLAN_FEATURES: Record<BasePlanKey, PlanFeatures> = {
  trial: {
    dashboard: false,
    leads: false,
    clients: false,
    orders: false,
    contracts: false,
    users: false,
    managers: false,
    agents: false,
    automations: false,
    notifications: true,
    settings: true,
    profile: true,
    sms: false,
  },
  basic: {
    dashboard: true,
    leads: true,
    clients: true,
    orders: false, // Orders not available in Basic plan
    contracts: true,
    users: true,
    managers: true, // Same as users
    agents: true,
    automations: false, // no advanced automations
    notifications: true,
    settings: true,
    profile: true,
    sms: true,
  },
  vip: {
    dashboard: true,
    leads: true,
    clients: true,
    orders: true,
    contracts: true,
    users: true,
    managers: true, // Same as users
    agents: true,
    automations: true,
    notifications: true,
    settings: true,
    profile: true,
    sms: true,
  },
  platinum: {
    dashboard: true,
    leads: true,
    clients: true,
    orders: true,
    contracts: true,
    users: true,
    managers: true, // Same as users
    agents: true,
    automations: true,
    notifications: true,
    settings: true,
    profile: true,
    sms: true,
  },
  enterprise: {
    dashboard: true,
    leads: true,
    clients: true,
    orders: true,
    contracts: true,
    users: true,
    managers: true, // Same as users
    agents: true,
    automations: true,
    notifications: true,
    settings: true,
    profile: true,
    sms: true,
    unlimitedEverything: true,
  },
};

/**
 * Company data structure for plan features calculation
 */
export type CompanyPlanData = {
  plan?: string | null;
};

/**
 * Get plan features for a company
 * 
 * @param company - Company object with plan property
 * @returns PlanFeatures - Feature permissions for the company's plan
 */
export function getPlanFeatures(company: CompanyPlanData | null | undefined): PlanFeatures {
  if (!company || !company.plan) {
    // Default to trial if no company or plan
    return PLAN_FEATURES.trial;
  }

  try {
    // Normalize plan key to get base plan
    const planKey = normalizePlanKey(company.plan);
    
    // TypeScript ensures planKey is BasePlanKey, which is a key of PLAN_FEATURES
    // Use type assertion since we know normalizePlanKey always returns a valid BasePlanKey
    const features = PLAN_FEATURES[planKey];
    
    if (features) {
      return features;
    }
    
    // Fallback to trial if features is somehow undefined
    console.warn(`[planFeatures] Plan features not found for key: ${planKey}, defaulting to trial. Company plan was: ${company.plan}`);
    return PLAN_FEATURES.trial;
  } catch (error) {
    console.error(`[planFeatures] Error getting plan features for plan: ${company.plan}`, error);
    return PLAN_FEATURES.trial;
  }
}

/**
 * Check if a specific feature is enabled for a company
 * 
 * @param company - Company object with plan property
 * @param feature - Feature key to check
 * @returns boolean - Whether the feature is enabled
 */
export function hasPlanFeature(
  company: CompanyPlanData | null | undefined,
  feature: FeatureKey
): boolean {
  const features = getPlanFeatures(company);
  return features[feature] === true;
}

